import axios from 'axios';
import rutas from '@/assets/utils/rutas.json';

const API = rutas.axios + 'api';

const mapServicios = servicios => ({
  atenciones: Array.isArray(servicios?.atenciones) ? servicios.atenciones.length > 0 : Boolean(servicios?.atenciones),
  imagenes: Array.isArray(servicios?.imagenes) ? servicios.imagenes.length > 0 : Boolean(servicios?.imagenes),
  laboratorios: Array.isArray(servicios?.laboratorios) ? servicios.laboratorios.length > 0 : Boolean(servicios?.laboratorios),
  terapias: Array.isArray(servicios?.terapias) ? servicios.terapias.length > 0 : Boolean(servicios?.terapias),
  procedimientos: Array.isArray(servicios?.procedimientos) ? servicios.procedimientos.length > 0 : Boolean(servicios?.procedimientos),
});

const parseJsonSafe = value => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    console.error('No se pudo parsear JSON del paquete asignado', error);
    return {};
  }
};

const normalizePersona = (paciente = {}) => {
  const persona = paciente.persona || {};
  const identificacion = paciente.identificacion || persona.identificacion || '';
  const partesNombre = [
    persona.primerNombre || paciente.primerNombre,
    persona.segundoNombre || paciente.segundoNombre,
    persona.primerApellido || paciente.primerApellido,
    persona.segundoApellido || paciente.segundoApellido,
  ].filter(Boolean);

  return {
    id: paciente.id ?? persona.id ?? null,
    identificacion,
    primerNombre: paciente.primerNombre || persona.primerNombre || '',
    segundoNombre: paciente.segundoNombre || persona.segundoNombre || '',
    primerApellido: paciente.primerApellido || persona.primerApellido || '',
    segundoApellido: paciente.segundoApellido || persona.segundoApellido || '',
    fechaNacimiento: persona.fechaNacimiento || paciente.fechaNacimiento || null,
    correo: persona.correo || paciente.correo || '',
    telefono: persona.telefono || paciente.telefono || '',
    persona,
    value: paciente.id ?? persona.id ?? identificacion,
    label: partesNombre.length ? partesNombre.join(' ') : identificacion,
  };
};

const normalizePaquete = paquete => {
  const jsonParsed = parseJsonSafe(paquete?.json_paquete || paquete?.jsonPaquete || paquete?.jsonParsed);
  const precio = paquete?.precio != null
    ? paquete.precio
    : (jsonParsed?.precio ?? jsonParsed?.precios?.valor ?? null);

  const codigo = paquete?.codigo ?? jsonParsed?.codigo ?? '';
  const nombre = paquete?.nombre ?? jsonParsed?.nombre ?? paquete?.text ?? 'Paquete sin nombre';
  const id = paquete?.id ?? jsonParsed?.id ?? null;

  return {
    id,
    token: paquete?.token ?? jsonParsed?.token ?? null,
    codigo: codigo || null,
    nombre,
    descripcion: paquete?.descripcion ?? jsonParsed?.descripcion ?? '',
    precio,
    jsonParsed,
    value: id ?? paquete?.token ?? jsonParsed?.token ?? null,
    text: [codigo, nombre].filter(Boolean).join(' - '),
  };
};

const normalizeServiciosDetalle = (detalle = {}) => {
  const resultado = {};
  if (!detalle || typeof detalle !== 'object') return resultado;

  Object.keys(detalle).forEach(key => {
    const lista = Array.isArray(detalle[key]) ? detalle[key] : [];
    resultado[key] = lista.map(item => ({ ...(item || {}) }));
  });

  return resultado;
};

const normalizeDocumentos = documentos => {
  if (!documentos) return { cedula: null, otro: null };
  if (typeof documentos === 'object' && !Array.isArray(documentos)) return documentos;
  if (Array.isArray(documentos)) {
    const acc = { cedula: null, otro: null };
    documentos.forEach(doc => {
      if (!doc || typeof doc !== 'object') return;
      if (!acc.cedula && /cedula/i.test(doc?.tipo || '')) acc.cedula = doc.url || doc.path || null;
      if (!acc.otro) acc.otro = doc.url || doc.path || null;
    });
    return acc;
  }
  return { cedula: null, otro: null };
};

const buildServiciosResumenFromJson = (json = {}) => {
  const servicios = json?.servicios || {};
  const definiciones = [
    { key: 'imagenes', title: 'Imágenes' },
    { key: 'laboratorio', title: 'Laboratorios' },
    { key: 'procedimientos', title: 'Procedimientos' },
    { key: 'terapias', title: 'Terapias' },
    { key: 'atencion_medica', title: 'Atenciones médicas' },
  ];

  const formatGenerico = item => {
    if (!item) return 'Sin descripción';
    const descripcion = item.descripcion || item.label || item.nombre || item.servicio || 'Sin descripción';
    const cantidad = Number(item.cantidad) > 1 ? ` (x${Number(item.cantidad)})` : '';
    const codigo = item.codigo ? ` [${item.codigo}]` : '';
    return `${descripcion}${codigo}${cantidad}`.trim();
  };

  const formatAtencion = item => {
    if (!item) return 'Especialidad: Sin especialidad';
    const especialidad = item.especialidad_text || item.especialidadDescripcion || item.descripcion || 'Sin especialidad';
    const modalidad = item.tipo || item.modalidadDescripcion || item.modalidad || 'Sin modalidad';
    const doctor = item.doctor || item.doctorNombre || 'Sin doctor asignado';
    const fecha = item.fecha ? ` | Fecha: ${item.fecha}` : '';
    return `Especialidad: ${especialidad} | Modalidad: ${modalidad} | Doctor asignado: ${doctor}${fecha}`;
  };

  return definiciones
    .map(({ key, title }) => {
      const lista = Array.isArray(servicios[key]) ? servicios[key] : [];
      if (!lista.length) return null;

      const items = lista.map(item => (key === 'atencion_medica' ? formatAtencion(item) : formatGenerico(item)));
      return { key, title, items };
    })
    .filter(Boolean);
};

const getListado = async (params = {}) => {
  const query = `
    query ConsultaPaquetesAsignados(
      $search: String
      $paciente: String
      $paquete: String
      $estado: String
      $fechaDesde: Date
      $fechaHasta: Date
      $page: Int
      $pageSize: Int
    ) {
      paquete_asignado(
        params: {
          search: $search
          paciente: $paciente
          paquete: $paquete
          estado: $estado
          fechaDesde: $fechaDesde
          fechaHasta: $fechaHasta
          page: $page
          pageSize: $pageSize
        }
      ) {
        status
        message
        total
        data {
          id
          token
          fechaAsignacion
          estado
          encuesta
          documentosFaltantes
          servicios {
            atenciones
            imagenes
            laboratorios
            terapias
            procedimientos
          }
          serviciosDetalle
          paciente {
            id
            identificacion
            primerNombre
            segundoNombre
            primerApellido
            segundoApellido
            persona {
              id
              identificacion
              primerNombre
              segundoNombre
              primerApellido
              segundoApellido
              fechaNacimiento
              correo
              telefono
            }
          }
          paquete {
            id
            token
            codigo
            nombre
            json_paquete
          }
        }
      }
    }
  `;

  const variables = {
    search: params.search || null,
    paciente: params.paciente || null,
    paquete: params.paquete || null,
    estado: params.estado || null,
    fechaDesde: params.fechaDesde || null,
    fechaHasta: params.fechaHasta || null,
    page: params.page || 1,
    pageSize: params.pageSize || 15,
  };

  try {
    const { data } = await axios.post(API, { query, variables });
    const response = data?.data?.paquete_asignado;
    const status = String(response?.status || '').toUpperCase();

    if (!response || !['SUCCESS', 'OK'].includes(status) || !Array.isArray(response.data)) {
      return { total: 0, data: [] };
    }

    const items = response.data.map(item => {
      const paciente = normalizePersona(item.paciente || {});
      const paquete = normalizePaquete(item.paquete || {});

      const serviciosDetalle = normalizeServiciosDetalle(item.serviciosDetalle || {});
      const tieneDetalle = Object.values(serviciosDetalle).some(lista => Array.isArray(lista) && lista.length);

      return {
        id: item.id,
        token: item.token || null,
        fechaAsignacion: item.fechaAsignacion,
        estado: item.estado,
        encuesta: item.encuesta ?? null,
        documentosFaltantes: item.documentosFaltantes || [],
        servicios: mapServicios(item.servicios),
        serviciosDetalle,
        serviciosResumen: tieneDetalle ? [] : buildServiciosResumenFromJson(paquete.jsonParsed),
        paciente,
        paquete,
        documentos: {},
      };
    });

    return {
      total: Number(response.total) || items.length,
      data: items,
    };
  } catch (error) {
    console.error('Error fetching paquetes asignados:', error);
    return { total: 0, data: [] };
  }
};

export const paquetesAsignadosService = {
  getListado,
  async getDetalle({ token } = {}) {
    if (!token) return null;

    const buildDetalleQuery = ({ includePersona, includeDocumentos }) => `
      query DetallePaqueteAsignado($token: String!) {
        paquete_asignado_by_token(token: $token) {
          status
          message
          data {
            id
            token
            fechaAsignacion
            estado
            encuesta
            ${includeDocumentos ? 'documentos' : ''}
            servicios {
              atenciones
              imagenes
              laboratorios
              terapias
              procedimientos
            }
            serviciosDetalle
            paciente {
              id
              identificacion
              primerNombre
              segundoNombre
              primerApellido
              segundoApellido
              ${includePersona
                ? `persona {
                  id
                  identificacion
                  primerNombre
                  segundoNombre
                  primerApellido
                  segundoApellido
                  fechaNacimiento
                  correo
                  telefono
                }`
                : ''}
            }
            paquete {
              id
              token
              codigo
              nombre
              json_paquete
            }
          }
        }
      }
    `;

    const queryDefinitions = [
      { includePersona: true, includeDocumentos: true },
      { includePersona: false, includeDocumentos: true },
      { includePersona: true, includeDocumentos: false },
      { includePersona: false, includeDocumentos: false },
    ];

    let lastErrorMessage = null;

    for (const definition of queryDefinitions) {
      const query = buildDetalleQuery(definition);
      const variables = { token };
      try {
        const { data } = await axios.post(API, { query, variables });

        if (Array.isArray(data?.errors) && data.errors.length) {
          const [firstError] = data.errors;
          if (firstError?.message) lastErrorMessage = firstError.message;
          continue;
        }

        const payload = data?.data?.paquete_asignado_by_token;
        if (!payload) continue;

        const status = String(payload?.status || 'success').toUpperCase();
        const detalle = payload?.data || null;
        if (!detalle) {
          if (payload?.message) lastErrorMessage = payload.message;
          if (status === 'SUCCESS') continue;
          continue;
        }

        const paciente = normalizePersona(detalle.paciente || {});
        const paquete = normalizePaquete(detalle.paquete || {});
        const serviciosDetalle = normalizeServiciosDetalle(detalle.serviciosDetalle || detalle.servicios_detalle || {});
        const tieneDetalle = Object.values(serviciosDetalle).some(lista => Array.isArray(lista) && lista.length);
        const documentos = normalizeDocumentos(detalle.documentos);
        const servicios = detalle.servicios || {};
        const serviciosResumen = tieneDetalle ? [] : buildServiciosResumenFromJson(paquete.jsonParsed);

        return {
          id: detalle.id,
          token: detalle.token || paquete.token || token,
          fechaAsignacion: detalle.fechaAsignacion || detalle.fecha_asignacion || null,
          estado: detalle.estado || null,
          encuesta: detalle.encuesta ?? null,
          paciente,
          paquete,
          serviciosDetalle,
        serviciosResumen,
          servicios,
          documentos,
        };
      } catch (error) {
        console.warn('Query detalle paquete asignado falló', error?.message || error);
        continue;
      }
    }

    throw new Error(lastErrorMessage || 'No se pudo obtener el detalle del paquete asignado');
  },
};
